import { faUserCheck, faUserXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableRow, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Props } from "./interfaces";
import AddNewDocument from "../../../../../containers/AddNewDocument/AddNewDocument";
import AttachedDocuments from "../../../../../containers/AttachedDocuments/AttachedDocuments";
import { AbsenceStatusIndicator } from "../../../../../containers/MyCalendar/AddEditAbsence/styles";
import useAuthorization from "../../../../../custom-hooks/useAuthorization/useAuthorization";
import { DocumentCategoryRef } from "../../../../../models/document-categories";
import { DocumentTypeRef } from "../../../../../models/document-types";
import { DocumentRef } from "../../../../../models/documents";
import { EmployeeRef } from "../../../../../models/employees";
import { TnAAbsenceSTO } from "../../../../../models/tna";
import CommentBubble from "../../../../../reusable-components/CommentBubble/CommentBubble";
import { TnAAbsencesApiService } from "../../../../../services/tna-absences/tna-absences-api.service";
import { AUTHORIZATIONS } from "../../../../../utils/constants/authorization-constants";
import { DOCUMENT_CATEGORIES, DOCUMENT_TYPES } from "../../../../../utils/constants/constants";
import { intlFormatDateShort } from "../../../../../utils/time-utils";
import { toastifyResponse } from "../../../../../utils/toastify-response/toastify-response";
import AbsenceButton from "../../../../MyCalendar/AddEditAbsence/AbsenceButton";
import EditAbsenceWrapper from "../../../../MyCalendar/AddEditAbsence/EditAbsenceWrapper/EditAbsenceWrapper";
import { StyledTableCell } from "../styles";

const AbsencesRow = ({
  item,
  getWidth,
  handleNotifyParent,
  minDate,
  maxDate,
  allowedOnlyApproveReject,
  allEmployeesSelected = false,
  counters,
}: Props) => {
  const theme = useTheme();

  const navigate = useNavigate();

  const { t } = useTranslation();
  const tRP = "components.my_calendar.add_absence";

  const allowedToApproveReject = useAuthorization([AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves]);

  const saveAbsenceDocument = useCallback(async (absence: TnAAbsenceSTO, document: DocumentRef) => {
    const absenceToSave = { ...absence, documentation: document };

    const response = await new TnAAbsencesApiService().saveItems([absenceToSave]);

    toastifyResponse(response, "failed");

    return response;
  }, []);

  const redirectToEeTimesheet = useCallback((employee: EmployeeRef | null) => {
    employee &&
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        },
      });
  }, []);

  const isAbsenceSignedOff = useMemo(() => {
    return (
      item?.fromDate &&
      minDate &&
      new Date(item?.fromDate.year, item.fromDate.month - 1, item.fromDate.day) < new Date(minDate)
    );
  }, [item, minDate]);

  return (
    <>
      <TableRow>
        <StyledTableCell width={getWidth("status")}>
          <Grid sx={{ width: "90%", margin: "0 auto", height: "55px", display: "flex", alignItems: "center" }}>
            <AbsenceStatusIndicator
              isRejected={item.isRejected}
              isCancelled={item.isCancelled}
              isApproved={item.isApproved}
              key={item.id}
            >
              <EditAbsenceWrapper
                employee={item.employee}
                handleNotifyParent={handleNotifyParent}
                absence={item}
                forManager
                allowedToApproveReject={allowedToApproveReject}
                minDate={minDate}
                maxDate={maxDate}
                allowedOnlyApproveReject={allowedOnlyApproveReject}
                viewMode={allEmployeesSelected}
                showAdditionalInfoMessage={allEmployeesSelected}
                additionalInfoMessage={`${t(`${tRP}.info_message`)}`}
              >
                <AbsenceButton
                  absence={item}
                  forManager
                  wrapContent
                />
              </EditAbsenceWrapper>
            </AbsenceStatusIndicator>
          </Grid>
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("employee")}
          align="center"
        >
          <Grid
            sx={{
              "&:hover, &:hover .MuiTypography-root": {
                cursor: "pointer",
              },
            }}
            onClick={() => {
              redirectToEeTimesheet(item.employee);
            }}
            title={"View timesheet"}
          >
            <Typography>{item.employee?.name}</Typography>
          </Grid>
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("absenceType")}
          align="center"
        >
          <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
            {item?.absenceType?.name && <Typography>{item.absenceType.name}</Typography>}
            {item?.comment && (
              <CommentBubble
                title={`${t(`${tRP}.fields.comment`)}:`}
                comment={item.comment}
              />
            )}
          </Grid>
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("fromDate")}
          align="center"
        >
          {item?.fromDate && (
            <Typography>
              {intlFormatDateShort(new Date(item.fromDate.year, item.fromDate.month - 1, item.fromDate.day))}
            </Typography>
          )}
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("toDate")}
          align="center"
        >
          {item?.toDate && (
            <Typography>
              {intlFormatDateShort(new Date(item.toDate.year, item.toDate.month - 1, item.toDate.day))}
            </Typography>
          )}
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("documentation")}
          align="center"
        >
          <Grid sx={{ display: "flex", width: "100%", justifyContent: "center", alignItems: "center" }}>
            {item?.documentation ? (
              <AttachedDocuments
                count={item.documentation?.filesCount}
                documentIDs={[item.documentation?.id]}
                buttonSize={"2x"}
                handleNotifyParent={handleNotifyParent}
                documentsReadOnlyFields={[
                  {
                    documentID: item.documentation?.id,
                    readOnlyFields: {
                      isEmployeeRO: true,
                      isCategoryRO: true,
                      isLegalEntityRO: true,
                      isTypeRO: true,
                      isDateRO: true,
                      isStatusRO: true,
                    },
                  },
                ]}
                hideReadOnlyFields={true}
                useSimpleView={isAbsenceSignedOff || !!item?.isApproved || !!item?.isRejected || !!item?.isCancelled}
                documentIsReadOnly={
                  isAbsenceSignedOff || !!item?.isApproved || !!item?.isRejected || !!item?.isCancelled
                }
              />
            ) : (
              <>
                {!allEmployeesSelected &&
                  !isAbsenceSignedOff &&
                  !item?.isApproved &&
                  !item?.isRejected &&
                  !item?.isCancelled && (
                    <AddNewDocument
                      buttonType="plusButton"
                      useMiniPlusButton={true}
                      relation={{
                        relatedEntity: item as TnAAbsenceSTO,
                        relationHandler: saveAbsenceDocument,
                      }}
                      handleNotifyParent={handleNotifyParent}
                      documentFieldsProps={{
                        legalEntity: item?.legalEntity ?? null,
                        category: DOCUMENT_CATEGORIES.OTHER as DocumentCategoryRef,
                        type: DOCUMENT_TYPES.AbsenceDocumentation as DocumentTypeRef,
                        employee: item?.employee ?? null,
                        documentDate: new Date(),
                      }}
                      isWidget
                    />
                  )}
              </>
            )}
          </Grid>
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("isApproved, isRejected")}
          align="center"
        >
          {item?.isApproved && !item?.isCancelled ? (
            <Typography title={`Approved by ${item?.modifiedByUser?.name}`}>{item?.modifiedByUser?.name}</Typography>
          ) : item?.isRejected && !item?.isCancelled ? (
            <Typography title={`Rejected by ${item?.modifiedByUser?.name}`}>{item?.modifiedByUser?.name}</Typography>
          ) : (
            <></>
          )}
        </StyledTableCell>

        <StyledTableCell
          width={getWidth("acceptedByClient, rejectedByClient")}
          align="center"
        >
          <Grid sx={{ display: "flex", flexDirection: "row", gap: "5px", flexWrap: "wrap" }}>
            {item?.acceptedRejectedByUser?.name && <Typography>{item.acceptedRejectedByUser.name}</Typography>}
            {item?.acceptedByClient ? (
              <Typography>
                <FontAwesomeIcon
                  icon={faUserCheck}
                  color={theme.palette.success.main}
                />
              </Typography>
            ) : item?.rejectedByClient ? (
              <Typography>
                <FontAwesomeIcon
                  icon={faUserXmark}
                  color={theme.palette.error.main}
                />
              </Typography>
            ) : (
              <></>
            )}
          </Grid>
        </StyledTableCell>
      </TableRow>
    </>
  );
};

export default AbsencesRow;
